'use strict';

declare global {
  interface Window {
    imageUtils: ImageUtils;
  }
}

export class ImageUtils {
  /** Callable with 'calljavascript' by the web browser */
  public resizeImage(content: string, width: number) {
    return new Promise((resolve) => {
      const image = new Image();
      image.onload = function () {
        const canvas = document.createElement('canvas');

        // calculate new height
        const aspectRatio: number = image.width / image.height;
        const newHeight: number = width / aspectRatio;

        canvas.width = width;
        canvas.height = newHeight;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(image, 0, 0, width, newHeight);

        const mimeType = content.substring(
          content.indexOf(':') + 1,
          content.indexOf(';'),
        );

        const result: string = canvas.toDataURL(mimeType, 1);

        // Removes the canvas
        canvas.remove();

        resolve(result);
      };

      image.src = content;
    });
  }

  public scaleImage(content: string, scaleFactor: number) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    return new Promise((resolve) => {
      const image = new Image();
      image.onload = function () {
        // Calculate new width and height
        const newWidth: number = Math.round(image.width * scaleFactor);
        const newHeight: number = Math.round(image.height * scaleFactor);

        // Get the image data of original image (pixels)
        const originalCanvas = document.createElement('canvas');
        originalCanvas.width = image.width;
        originalCanvas.height = image.height;

        const originalCtx = originalCanvas.getContext('2d');
        originalCtx.drawImage(image, 0, 0);

        const imageData = originalCtx.getImageData(
          0,
          0,
          originalCanvas.width,
          originalCanvas.height,
        );

        // Resize the image
        const resizedImageData = self.resizeBicubic(
          imageData,
          newWidth,
          newHeight,
        );

        const canvas = document.createElement('canvas');
        canvas.width = newWidth;
        canvas.height = newHeight;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.putImageData(resizedImageData, 0, 0);

        const mimeType = content.substring(
          content.indexOf(':') + 1,
          content.indexOf(';'),
        );

        const result = canvas.toDataURL(mimeType, 1);

        // Removes the canvas
        canvas.remove();
        originalCanvas.remove();

        resolve(result);
      };

      image.src = content;
    });
  }

  public convertToJpg(content: string) {
    return new Promise((resolve) => {
      const image = new Image();
      image.onload = function () {
        // Resize the image
        const canvas = document.createElement('canvas');

        canvas.width = image.width;
        canvas.height = image.height;

        const ctx = canvas.getContext('2d');

        // Fill with white background (JPEG doesn't support transparency)
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(image, 0, 0);

        const result = canvas.toDataURL('image/jpeg', 1);

        // Removes the canvas
        canvas.remove();

        resolve(result);
      };

      image.src = content;
    });
  }

  public getImageDimension(content: string) {
    return new Promise((resolve) => {
      const image = new Image();
      image.onload = function () {
        const result = JSON.stringify({
          width: image.width,
          height: image.height,
        });
        resolve(result);
      };

      image.src = content;
    });
  }

  // Helper to get pixel RGBA from ImageData at (x, y)
  private getPixel(imgData: ImageData, x: number, y: number) {
    const { width, height, data } = imgData;
    x = Math.min(width - 1, Math.max(0, Math.floor(x)));
    y = Math.min(height - 1, Math.max(0, Math.floor(y)));
    const idx = (y * width + x) * 4;
    return data.slice(idx, idx + 4); // [r,g,b,a]
  }

  // Helper to set pixel RGBA to ImageData at (x, y)
  private setPixel(imgData: ImageData, x: number, y: number, rgba: number[]) {
    const { width, data } = imgData;
    const idx = (y * width + x) * 4;
    for (let i = 0; i < 4; i++) data[idx + i] = rgba[i];
  }

  // Linear interpolate between two values
  private lerp(a: number, b: number, t: number) {
    return a + (b - a) * t;
  }

  // Clamp value between 0 and 255
  private clamp(v: number) {
    return Math.min(255, Math.max(0, v));
  }

  // Nearest neighbor resize
  private resizeNearest(
    srcImg: ImageData,
    newWidth: number,
    newHeight: number,
  ) {
    const dstImg = new ImageData(newWidth, newHeight);
    const xRatio = srcImg.width / newWidth;
    const yRatio = srcImg.height / newHeight;

    for (let y = 0; y < newHeight; y++) {
      for (let x = 0; x < newWidth; x++) {
        const srcX = Math.floor(x * xRatio);
        const srcY = Math.floor(y * yRatio);
        const pixel = this.getPixel(srcImg, srcX, srcY);
        this.setPixel(dstImg, x, y, pixel as unknown as number[]);
      }
    }
    return dstImg;
  }

  // Bilinear interpolation resize
  private resizeBilinear(
    srcImg: ImageData,
    newWidth: number,
    newHeight: number,
  ) {
    const dstImg = new ImageData(newWidth, newHeight);
    const xRatio = (srcImg.width - 1) / newWidth;
    const yRatio = (srcImg.height - 1) / newHeight;

    for (let y = 0; y < newHeight; y++) {
      const srcY = y * yRatio;
      const yFloor = Math.floor(srcY);
      const yDiff = srcY - yFloor;

      for (let x = 0; x < newWidth; x++) {
        const srcX = x * xRatio;
        const xFloor = Math.floor(srcX);
        const xDiff = srcX - xFloor;

        // Get four neighboring pixels
        const p00 = this.getPixel(srcImg, xFloor, yFloor);
        const p10 = this.getPixel(srcImg, xFloor + 1, yFloor);
        const p01 = this.getPixel(srcImg, xFloor, yFloor + 1);
        const p11 = this.getPixel(srcImg, xFloor + 1, yFloor + 1);

        // Interpolate each channel
        const rgba = [0, 0, 0, 0];
        for (let i = 0; i < 4; i++) {
          const r1 = this.lerp(p00[i], p10[i], xDiff);
          const r2 = this.lerp(p01[i], p11[i], xDiff);
          rgba[i] = this.clamp(this.lerp(r1, r2, yDiff));
        }

        this.setPixel(dstImg, x, y, rgba);
      }
    }
    return dstImg;
  }

  // Cubic Hermite spline for Bicubic interpolation helper
  private cubicHermite(A: number, B: number, C: number, D: number, t: number) {
    const a = -A / 2 + (3 * B) / 2 - (3 * C) / 2 + D / 2;
    const b = A - (5 * B) / 2 + 2 * C - D / 2;
    const c = -A / 2 + C / 2;
    const d = B;
    return a * t * t * t + b * t * t + c * t + d;
  }

  // Bicubic interpolation resize
  protected resizeBicubic(
    srcImg: ImageData,
    newWidth: number,
    newHeight: number,
  ) {
    const dstImg = new ImageData(newWidth, newHeight);
    const xRatio = srcImg.width / newWidth;
    const yRatio = srcImg.height / newHeight;

    for (let y = 0; y < newHeight; y++) {
      const srcY = y * yRatio;
      const yInt = Math.floor(srcY);
      const yFrac = srcY - yInt;

      for (let x = 0; x < newWidth; x++) {
        const srcX = x * xRatio;
        const xInt = Math.floor(srcX);
        const xFrac = srcX - xInt;

        const rgba = [0, 0, 0, 0];
        for (let channel = 0; channel < 4; channel++) {
          // Collect 4 rows of 4 pixels each for interpolation
          const arr = [];
          for (let m = -1; m <= 2; m++) {
            const row = [];
            for (let n = -1; n <= 2; n++) {
              row.push(this.getPixel(srcImg, xInt + n, yInt + m)[channel]);
            }
            // Interpolate along x
            arr.push(this.cubicHermite(row[0], row[1], row[2], row[3], xFrac));
          }
          // Interpolate along y
          const value = this.cubicHermite(
            arr[0],
            arr[1],
            arr[2],
            arr[3],
            yFrac,
          );
          rgba[channel] = this.clamp(value);
        }

        this.setPixel(dstImg, x, y, rgba);
      }
    }
    return dstImg;
  }
}
